/* Heirloom Portfolio — paginated album-page grids. Photos sit inside
   thick cream "mat" borders with hand-typeset plate captions beneath. */

import { useEffect } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { getFocalStyle } from "@/lib/focal";
import { optimizeImageUrl } from "@/lib/imageOptim";
import { EditorialSkeleton } from "../editorial/_EditorialSkeleton";

export default function HeirloomPortfolio() {
  const { settings, loading } = useSiteSettings();
  const port = settings?.portfolio_images || [];
  const focals = settings?.portfolio_focal_points || [];
  const studioName = settings?.studio_name || "Studio";
  useEffect(() => { document.title = `${studioName} — Album Archive`; }, [studioName]);
  if (loading && !settings) return <EditorialSkeleton eyebrow="Opening the archive." />;

  // Paginate into album-spreads of 4 plates each
  const spreads = [];
  for (let i = 0; i < port.length; i += 4) spreads.push(port.slice(i, i + 4));

  return (
    <div data-testid="heirloom-portfolio" style={{ background: "var(--tenant-bg, #F8F2EA)", color: "var(--tenant-fg, #2E2419)" }}>
      <section className="px-6 lg:px-16 pt-20 pb-12 text-center">
        <div className="text-[10px] uppercase tracking-[0.5em] mb-4" style={{ color: "var(--tenant-accent)" }}>
          The Archive
        </div>
        <h1 className="italic" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.5rem, 5vw, 4rem)" }}>
          {settings?.portfolio_title || "Selected plates."}
        </h1>
      </section>

      <div className="max-w-[1280px] mx-auto px-6 lg:px-16 pb-32 space-y-24">
        {spreads.map((spread, si) => (
          <div key={si}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {spread.map((src, i) => (
                <figure key={i}>
                  <div className="aspect-[3/4] overflow-hidden border-8"
                       style={{ borderColor: "color-mix(in srgb, var(--tenant-bg) 80%, var(--tenant-fg) 20%)" }}>
                    <img src={optimizeImageUrl(src, { w: 800 })} alt=""
                      className="w-full h-full object-cover"
                      style={getFocalStyle(focals, i)}
                      loading={si === 0 && i < 2 ? "eager" : "lazy"} decoding="async" />
                  </div>
                  <figcaption className="text-center text-[9px] uppercase tracking-[0.32em] mt-3 italic"
                              style={{ color: "var(--tenant-fg-soft)", fontFamily: "var(--tenant-serif)" }}>
                    Plate №{String(si * 4 + i + 1).padStart(2, "0")}
                  </figcaption>
                </figure>
              ))}
            </div>
            <div className="text-center text-[10px] uppercase tracking-[0.42em] pt-10"
                 style={{ color: "var(--tenant-fg-mute)" }}>
              — Spread {String(si + 1).padStart(2, "0")} —
            </div>
          </div>
        ))}
        {port.length === 0 && (
          <p className="text-center italic" style={{ color: "var(--tenant-fg-soft)" }}>
            The archive is being bound. Plates forthcoming.
          </p>
        )}
      </div>
    </div>
  );
}
